import React, { useEffect, useState } from 'react';
import css from './FeaturedHomes.module.css';
import BrandIconCard from '../BrandIconCard/BrandIconCard';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import { NamedLink } from '../../components';
import { createSlug } from '../../util/urlHelpers';
import {
  getCloudinaryCoverPhoto,
  getCloudinaryImageUrl,
  getCoverPhoto,
} from '../../util/listingImages';
import { APPLY_FOR_INVITE_URL } from '../../util/applyForInvite';
import { useMediaQuery } from '../../util/uiHelpers';
import SwiperCore, { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
SwiperCore.use([Navigation, Pagination]);

// Import Swiper CSS only on client side (webpack will handle this for SSR)
if (typeof window !== 'undefined') {
  require('swiper/swiper-bundle.css');
}
const getNorthAmericaLocationLabel = address => {
  if (!address) return '';

  const parts = address
    .split(',')
    .map(p => p.trim())
    .filter(Boolean);

  if (parts.length < 2) {
    return address;
  }

  const country = parts[parts.length - 1];
  let stateOrRegion = parts.length >= 2 ? parts[parts.length - 2] : '';
  const city = parts.length >= 3 ? parts[parts.length - 3] : '';

  // Remove postal codes from state/region
  // Remove US ZIP codes (e.g., 12345 or 12345-6789)
  stateOrRegion = stateOrRegion
    .replace(/\b\d{5}(?:-\d{4})?\b/g, '')
    // Remove Canadian postal codes (e.g., A0G 3E0)
    .replace(/\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/gi, '')
    // Remove UK postal codes (e.g., SW1A 1AA)
    .replace(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}\b/gi, '')
    // Remove other common postal code patterns
    .replace(/\b\d{4,6}\b/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    // Remove leading postal codes not caught above (e.g., "841 00 Ermoupolis" → "Ermoupolis")
    .replace(/^\d[\d\s]*\s+/, '')
    .trim();

  // Check if it's USA
  const usaCountries = [
    'United States',
    'United States of America',
    'USA',
    'US',
  ];

  const isUSA = usaCountries.some(
    c => c.toLowerCase() === country.toLowerCase()
  );

  if (isUSA && city && stateOrRegion) {
    // USA: show "City, State"
    return `${city}, ${stateOrRegion}`;
  }

  if (!isUSA && city && country) {
    // International: show "City, Country"
    // If the parsed "city" contains digits it's a street (e.g. "Psaron Street 22"),
    // so use stateOrRegion (which is the actual city) instead.
    if (/\d/.test(city)) {
      return stateOrRegion ? `${stateOrRegion}, ${country}` : country;
    }
    return `${city}, ${country}`;
  }

  // Fallbacks
  if (city && stateOrRegion) {
    return `${city}, ${stateOrRegion}`;
  }
  if (city) return city;
  if (stateOrRegion) return `${stateOrRegion}, ${country}`;
  return country || address;
};
// Matches the same breakpoint as before, but without reading window.innerWidth
// (a layout read that forces a synchronous reflow on mount and on every resize).
const useIsMobile = () => useMediaQuery('(max-width: 768px)');
function truncateText(text, maxLength) {
  if (text.length <= maxLength) {
    return text; // Return the original text if it's within or equal to the maxLength
  } else {
    return text.slice(0, maxLength) + '...'; // Truncate the text and add ellipsis
  }
}

const FeaturedHomes = ({ projects, currentUser, onToggleFavorite }) => {
  const favorites =
    currentUser?.attributes?.profile?.publicData?.favorites || [];
  const [favoriteIds, setFavoriteIds] = React.useState(favorites);
  const isMobile = useIsMobile();
  const isLoggedIn = !!currentUser?.id?.uuid;
  // Sync favoriteIds with currentUser when it changes
  useEffect(() => {
    const updatedFavorites =
      currentUser?.attributes?.profile?.publicData?.favorites || [];
    setFavoriteIds(updatedFavorites);
  }, [currentUser]);

  const handleToggleFavorite = (e, projectId) => {
    e.preventDefault(); // Prevent default link behavior
    e.stopPropagation(); // Prevent card click navigation
    const currentFavorites =
      currentUser?.attributes?.profile?.publicData?.favorites ||
      favoriteIds ||
      [];
    const isFavorite = currentFavorites.includes(projectId);

    // Build new favorites array using current and incoming project id
    const updatedFavorites = isFavorite
      ? currentFavorites.filter(id => id !== projectId)
      : [...currentFavorites, projectId];
    setFavoriteIds(updatedFavorites);

    // Save to user profile
    if (!onToggleFavorite) {
      console.warn('onToggleFavorite handler is missing');
      return;
    }
    const actionPayload = {
      publicData: {
        favorites: updatedFavorites,
      },
    };
    onToggleFavorite(actionPayload).then(() => {
      setFavoriteIds(updatedFavorites);
    });
  };

  return (
    <section className={css.container}>
      <div className={css.header}>
        <h2>Featured Homes</h2>
        <NamedLink className={css.viewAll} name="SearchPage">
          View all
        </NamedLink>
      </div>
      {!isMobile && (
        <div className={css.grid}>
          {projects.slice(0, 5).map(project => {
            const publicData = project.attributes?.publicData || {};
            const cloudinaryCoverPhotoUrl = getCloudinaryImageUrl(
              getCloudinaryCoverPhoto(publicData)
            );
            const image = getCoverPhoto(project.images, publicData);
            const imageVariants = image?.attributes?.variants;
            const scaledMediumVariant = imageVariants?.['scaled-medium'];
            const imageUrl =
              cloudinaryCoverPhotoUrl ||
              scaledMediumVariant?.url ||
              scaledMediumVariant ||
              '';

            if (!imageUrl) return null;

            const projectId = project.id?.uuid || project.id;
            const address = publicData?.location?.address;
            const locationShow = getNorthAmericaLocationLabel(address);
            const isFavorite = favoriteIds.includes(projectId);
            const title = project.attributes?.title || '';
            const slug = createSlug(title);
            const CardLink = isLoggedIn ? NamedLink : 'a';
            const cardLinkProps = isLoggedIn
              ? { name: 'ListingPage', params: { id: projectId, slug } }
              : { href: APPLY_FOR_INVITE_URL, target: '_blank', rel: 'noopener noreferrer' };

            return (
              <CardLink
                key={`${project.id?.uuid || project.id}`}
                {...cardLinkProps}
                className={css.card}
              >
                <div className={css.imageWrapper}>
                  <img src={imageUrl} alt={title} className={css.image} loading="lazy" decoding="async" />
                  <div className={css.tag}>{locationShow}</div>
                  <button
                    type="button"
                    className={css.heart}
                    onClick={e => handleToggleFavorite(e, projectId)}
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    aria-pressed={isFavorite}
                  >
                    <BrandIconCard
                      type={isFavorite ? 'filledheart' : 'unfilledheart'}
                    />
                  </button>
                </div>

                <div className={css.details}>
                  <p className={css.price}>
                    $
                    {Number(
                      project.attributes?.price?.amount || 0
                    ).toLocaleString('en-US')}{' '}
                    <span>Monthly</span>
                  </p>
                  <h3 className={css.title}>{title}</h3>
                  {(publicData?.NumberofBedrooms ||
                    publicData?.NumberofBathrooms ||
                    publicData?.ageSuitability) && (
                    <div className={css.iconsContainer}>
                      {publicData?.NumberofBedrooms && (
                        <span className={css.icon}>
                          <IconCard brand="bed" />{' '}
                          {publicData?.NumberofBedrooms}
                        </span>
                      )}
                      {publicData?.NumberofBathrooms && (
                        <span className={css.icon}>
                          {' '}
                          <IconCard brand="toy" />{' '}
                          {publicData?.NumberofBathrooms}
                        </span>
                      )}
                      {publicData?.maximumGuests && (
                        <span className={css.icon}>
                          {' '}
                          <IconCard brand="listingUser" />
                          {publicData?.maximumGuests}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </CardLink>
            );
          })}
        </div>
      )}
      {isMobile && (
        <Swiper
          spaceBetween={16}
          slidesPerView={1.18}
          centeredSlides={false}
          slidesOffsetBefore={12}
          slidesOffsetAfter={12}
          modules={[Navigation]}
          navigation={false}
          breakpoints={{
            0: {
              slidesPerView: 1.22,
              slidesOffsetBefore: 0,
              slidesOffsetAfter: 0,
            },
            380: {
              slidesPerView: 1.28,
              slidesOffsetBefore: 10,
              slidesOffsetAfter: 10,
            },
            430: {
              slidesPerView: 1.18,
              slidesOffsetBefore: 12,
              slidesOffsetAfter: 12,
            },
            500: {
              slidesPerView: 1.25,
              slidesOffsetBefore: 16,
              slidesOffsetAfter: 16,
            },
          }}
        >
          {projects.map(project => {
            const publicData = project.attributes?.publicData || {};
            const cloudinaryCoverPhotoUrl = getCloudinaryImageUrl(
              getCloudinaryCoverPhoto(publicData)
            );
            const image = getCoverPhoto(project.images, publicData);
            const imageVariants = image?.attributes?.variants;
            const scaledMediumVariant = imageVariants?.['scaled-medium'];
            const imageUrl =
              cloudinaryCoverPhotoUrl ||
              scaledMediumVariant?.url ||
              scaledMediumVariant ||
              '';

            if (!imageUrl) return null;

            const projectId = project.id?.uuid || project.id;
            const address = publicData?.location?.address;
            const locationShow = getNorthAmericaLocationLabel(address);
            const isFavorite = favoriteIds.includes(projectId);
            const title = project.attributes?.title || '';
            const slug = createSlug(title);
            const CardLink = isLoggedIn ? NamedLink : 'a';
            const cardLinkProps = isLoggedIn
              ? { name: 'ListingPage', params: { id: projectId, slug } }
              : { href: APPLY_FOR_INVITE_URL, target: '_blank', rel: 'noopener noreferrer' };

            return (
              <SwiperSlide key={project.id?.uuid || project.id}>
                <CardLink
                  key={`${project.id?.uuid || project.id}`}
                  {...cardLinkProps}
                  className={css.card}
                >
                  <div className={css.imageWrapper}>
                    <img src={imageUrl} alt={title} className={css.image} loading="lazy" decoding="async" />
                    <div className={css.tag}>{locationShow}</div>

                    <button
                      type="button"
                      className={css.heart}
                      onClick={e => handleToggleFavorite(e, projectId)}
                      onMouseDown={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      aria-pressed={isFavorite}
                    >
                      <BrandIconCard
                        type={isFavorite ? 'filledheart' : 'unfilledheart'}
                      />
                    </button>
                  </div>

                  <div className={css.details}>
                    <p className={css.price}>
                      $
                      {Number(
                        project.attributes?.price?.amount || 0
                      ).toLocaleString('en-US')}{' '}
                      Monthly
                    </p>

                    <h3 className={css.title}>{title}</h3>

                    {(publicData?.NumberofBedrooms ||
                      publicData?.NumberofBathrooms ||
                      publicData?.ageSuitability) && (
                      <div className={css.iconsContainer}>
                        {publicData?.NumberofBedrooms && (
                          <span className={css.icon}>
                            <IconCard brand="bed" />{' '}
                            {publicData?.NumberofBedrooms}
                          </span>
                        )}

                        {publicData?.NumberofBathrooms && (
                          <span className={css.icon}>
                            <IconCard brand="toy" />{' '}
                            {publicData?.NumberofBathrooms}
                          </span>
                        )}

                        {publicData?.maximumGuests && (
                          <span className={css.icon}>
                            <IconCard brand="listingUser" />
                            {publicData?.maximumGuests}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </CardLink>
              </SwiperSlide>
            );
          })}
        </Swiper>
      )}
    </section>
  );
};

export default FeaturedHomes;
